import { QueryHookOptions, QueryResult, useQuery } from '@apollo/client';
import { FetchEntityCountsResult } from '@jupiterone/jupiter-web-toolkit/types';
import { useMemo } from 'react';
import { FetchEntityCounts } from '../../../graphql/queries/queries';
import {
  IntegrationDefinitionsQuery,
  IntegrationDefinitionsResult,
  IntegrationInstancesQuery,
  IntegrationInstancesResult,
} from '../../../integrations';
import getIntegrationInstanceDisplayName from '../../../integrations/getIntegrationInstanceDisplayName';
import { PermissionType } from './types';

export interface EntitySearchData {
  value: string | boolean;
  displayName?: string;
}

interface SearchHandler {
  query: any;
  options: QueryHookOptions<any>;
  reducer: (data: any) => EntitySearchData[];
}

interface SearchHandlers {
  [key: string]: SearchHandler;
}

export default function usePermissionEntitySearch(
  searchType: string
): QueryResult<EntitySearchData[] | null> {
  const handler = SEARCH_HANDLERS[searchType] || {
    query: IntegrationInstancesQuery,
    options: { skip: true },
  };
  const queryResult = useQuery(handler.query, handler.options);
  const data = useMemo(() => {
    if (handler.options.skip) return [];
    return queryResult.data && !queryResult.loading
      ? handler.reducer(queryResult.data)
      : [];
  }, [queryResult.data, queryResult.loading, searchType]);

  return {
    ...queryResult,
    data,
  };
}

const SEARCH_HANDLERS: SearchHandlers = {
  [PermissionType.class]: {
    query: FetchEntityCounts,
    options: {},
    reducer: (data: FetchEntityCountsResult): EntitySearchData[] =>
      Object.keys(data.allEntityCounts.classCounts).map((name) => ({
        value: name,
      })),
  },
  [PermissionType.type]: {
    query: FetchEntityCounts,
    options: {},
    reducer: (data: FetchEntityCountsResult): EntitySearchData[] =>
      Object.keys(data.allEntityCounts.typeCounts).map((name) => ({
        value: name,
      })),
  },
  [PermissionType.integrationClass]: {
    query: IntegrationDefinitionsQuery,
    options: {},
    reducer: (data: IntegrationDefinitionsResult): EntitySearchData[] =>
      data.integrationDefinitions.definitions
        .reduce(
          (acc: EntitySearchData[], definition) => [
            ...acc,
            ...definition.integrationClass.map((c) => ({ value: c })),
          ],
          []
        )
        .filter(
          (item, index, arr) =>
            arr.findIndex((i) => i.value === item.value) === index
        ),
  },
  [PermissionType.integrationType]: {
    query: IntegrationDefinitionsQuery,
    options: {},
    reducer: (data: IntegrationDefinitionsResult): EntitySearchData[] =>
      data.integrationDefinitions.definitions.map((definition) => ({
        value: definition.integrationType,
      })),
  },
  [PermissionType.integrationInstanceId]: {
    query: IntegrationInstancesQuery,
    options: {},
    reducer: (data: IntegrationInstancesResult): EntitySearchData[] =>
      data.integrationInstances.instances.map((instance) => ({
        value: instance.id,
        displayName: getIntegrationInstanceDisplayName(
          instance,
          data.integrationDefinitions.definitions
        ),
      })),
  },
};
